// CONTRACT:C2-RENDER-ENGINE.1.0
/** Time controller — the single-clock state the rAF loop advances.
 *
 *  Pure logic, no rAF/GPU: holds `now`, the visible `window`, and `playbackRate`,
 *  and derives time-scrub advancement and window membership. The engine's one
 *  rAF tick calls `advance(dtMs)` and reads `now()`; staleness evaluation
 *  (valid_time.stale) is delegated to the entity store using `now()`.
 *
 *  This file is UNIT-TESTED.
 */

/** The clock state set via RenderEngine.setTime. */
export interface TimeState {
  /** Current playback time (epoch ms). */
  now: number;
  /** Inclusive visible time window [start, end] (epoch ms). */
  window: [number, number];
  /** Playback rate multiplier (1 = realtime, 0 = paused, <0 = reverse). */
  playbackRate: number;
}

function assertFinite(n: number, what: string): void {
  if (!Number.isFinite(n)) throw new RangeError(`${what} must be finite, got ${n}`);
}

/** Single time controller. One instance per engine; the rAF loop owns it. */
export class TimeController {
  private state: TimeState;

  constructor(initial?: Partial<TimeState>) {
    const now = initial?.now ?? Date.now();
    this.state = {
      now,
      window: initial?.window ?? [now, now],
      playbackRate: initial?.playbackRate ?? 1,
    };
    this.validate(this.state);
  }

  /** Replace the full clock state (RenderEngine.setTime). */
  set(state: TimeState): void {
    this.validate(state);
    this.state = {
      now: state.now,
      window: [state.window[0], state.window[1]],
      playbackRate: state.playbackRate,
    };
  }

  now(): number {
    return this.state.now;
  }

  window(): [number, number] {
    return [this.state.window[0], this.state.window[1]];
  }

  playbackRate(): number {
    return this.state.playbackRate;
  }

  /** Advance the clock by a real-frame delta (ms), scaled by playbackRate.
   *  Returns the new `now`. A paused clock (rate 0) does not move. The clock
   *  is clamped to the visible window so scrub playback halts at the edges. */
  advance(realDtMs: number): number {
    assertFinite(realDtMs, "dt");
    const next = this.state.now + realDtMs * this.state.playbackRate;
    const [lo, hi] = this.state.window;
    this.state.now = Math.min(hi, Math.max(lo, next));
    return this.state.now;
  }

  /** Whether an instant (epoch ms) falls within the visible window. */
  inWindow(t: number): boolean {
    return t >= this.state.window[0] && t <= this.state.window[1];
  }

  /** Normalized position of `now` within the window in [0,1]. 0 if degenerate. */
  progress(): number {
    const [lo, hi] = this.state.window;
    if (hi <= lo) return 0;
    return (this.state.now - lo) / (hi - lo);
  }

  private validate(state: TimeState): void {
    assertFinite(state.now, "now");
    assertFinite(state.window[0], "window start");
    assertFinite(state.window[1], "window end");
    assertFinite(state.playbackRate, "playbackRate");
    if (state.window[1] < state.window[0]) {
      throw new RangeError("window end must be >= window start");
    }
  }
}
